import type { DiagramData } from '@/data/lessons';

interface PlayDiagramProps {
  diagram: DiagramData;
}

function FootballField() {
  return (
    <>
      {[20, 40, 60, 80].map(y => (
        <line key={y} x1="5" y1={y} x2="95" y2={y} stroke="hsl(210, 40%, 25%)" strokeWidth="0.3" strokeDasharray="2,2" />
      ))}
      {/* LOS - prominent yellow line */}
      <line x1="5" y1="60" x2="95" y2="60" stroke="hsl(48, 96%, 53%)" strokeWidth="0.8" />
      <text x="2" y="59" fill="hsl(48, 96%, 53%)" fontSize="3" fontFamily="Kanit" fontWeight="700">LOS</text>
    </>
  );
}

function SoccerPitch() {
  return (
    <>
      <rect x="3" y="3" width="94" height="84" fill="none" stroke="hsl(140, 30%, 40%)" strokeWidth="0.4" />
      <line x1="3" y1="45" x2="97" y2="45" stroke="hsl(140, 30%, 40%)" strokeWidth="0.4" />
      <circle cx="50" cy="45" r="8" fill="none" stroke="hsl(140, 30%, 40%)" strokeWidth="0.4" />
      <circle cx="50" cy="45" r="0.8" fill="hsl(140, 30%, 40%)" />
      <rect x="25" y="3" width="50" height="14" fill="none" stroke="hsl(140, 30%, 40%)" strokeWidth="0.4" />
      <rect x="25" y="73" width="50" height="14" fill="none" stroke="hsl(140, 30%, 40%)" strokeWidth="0.4" />
      <rect x="35" y="3" width="30" height="5" fill="none" stroke="hsl(140, 30%, 40%)" strokeWidth="0.3" />
      <rect x="35" y="82" width="30" height="5" fill="none" stroke="hsl(140, 30%, 40%)" strokeWidth="0.3" />
      <rect x="42" y="1" width="16" height="2" fill="none" stroke="hsl(140, 40%, 50%)" strokeWidth="0.5" />
      <rect x="42" y="87" width="16" height="2" fill="none" stroke="hsl(140, 40%, 50%)" strokeWidth="0.5" />
    </>
  );
}

function BasketballCourt() {
  return (
    <>
      <rect x="5" y="3" width="90" height="84" fill="none" stroke="hsl(25, 40%, 35%)" strokeWidth="0.4" />
      <line x1="5" y1="45" x2="95" y2="45" stroke="hsl(25, 40%, 35%)" strokeWidth="0.4" />
      <circle cx="50" cy="45" r="6" fill="none" stroke="hsl(25, 40%, 35%)" strokeWidth="0.4" />
      <path d="M 22 3 L 22 12 A 20 20 0 0 0 78 12 L 78 3" fill="none" stroke="hsl(25, 40%, 35%)" strokeWidth="0.4" />
      <path d="M 22 87 L 22 78 A 20 20 0 0 1 78 78 L 78 87" fill="none" stroke="hsl(25, 40%, 35%)" strokeWidth="0.4" />
      <rect x="33" y="3" width="34" height="16" fill="none" stroke="hsl(25, 40%, 35%)" strokeWidth="0.4" />
      <rect x="33" y="71" width="34" height="16" fill="none" stroke="hsl(25, 40%, 35%)" strokeWidth="0.4" />
      <circle cx="50" cy="19" r="6" fill="none" stroke="hsl(25, 40%, 35%)" strokeWidth="0.3" strokeDasharray="2,2" />
      <circle cx="50" cy="71" r="6" fill="none" stroke="hsl(25, 40%, 35%)" strokeWidth="0.3" strokeDasharray="2,2" />
      <circle cx="50" cy="6" r="1.5" fill="none" stroke="hsl(0, 60%, 50%)" strokeWidth="0.5" />
      <line x1="45" y1="4" x2="55" y2="4" stroke="hsl(0, 60%, 50%)" strokeWidth="0.5" />
      <circle cx="50" cy="84" r="1.5" fill="none" stroke="hsl(0, 60%, 50%)" strokeWidth="0.5" />
      <line x1="45" y1="86" x2="55" y2="86" stroke="hsl(0, 60%, 50%)" strokeWidth="0.5" />
    </>
  );
}

function BaseballDiamond() {
  // Outfield grass arc + infield diamond, plate-down orientation
  return (
    <>
      {/* Outfield arc */}
      <path d="M 5 50 Q 50 -5 95 50" fill="none" stroke="hsl(140, 30%, 40%)" strokeWidth="0.4" />
      {/* Foul lines from home plate */}
      <line x1="50" y1="78" x2="6" y2="22" stroke="hsl(0, 0%, 70%)" strokeWidth="0.4" />
      <line x1="50" y1="78" x2="94" y2="22" stroke="hsl(0, 0%, 70%)" strokeWidth="0.4" />
      {/* Infield diamond (1B-2B-3B-Home) */}
      <polygon points="72,56 50,42 28,56 50,70" fill="none" stroke="hsl(25, 60%, 45%)" strokeWidth="0.5" />
      {/* Pitcher's mound */}
      <circle cx="50" cy="60" r="2.2" fill="none" stroke="hsl(25, 60%, 45%)" strokeWidth="0.4" />
      {/* Bases */}
      <rect x="48.5" y="76.5" width="3" height="3" fill="hsl(0, 0%, 90%)" />
      <rect x="70.5" y="54.5" width="3" height="3" fill="hsl(0, 0%, 90%)" />
      <rect x="48.5" y="40.5" width="3" height="3" fill="hsl(0, 0%, 90%)" />
      <rect x="26.5" y="54.5" width="3" height="3" fill="hsl(0, 0%, 90%)" />
    </>
  );
}

export function PlayDiagram({ diagram }: PlayDiagramProps) {
  const sport = diagram.sport || 'football';

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-secondary rounded-xl p-4 relative overflow-hidden">
        <svg
          viewBox="0 0 100 92"
          preserveAspectRatio="xMidYMid meet"
          className="w-full"
          style={{ minHeight: 200 }}
        >
          {sport === 'soccer' && <SoccerPitch />}
          {sport === 'basketball' && <BasketballCourt />}
          {sport === 'baseball' && <BaseballDiamond />}
          {sport === 'football' && <FootballField />}

          {/* Routes / movement arrows */}
          {diagram.routes?.map((route, i) => (
            <line
              key={`route-${i}`}
              x1={route.fromX}
              y1={route.fromY}
              x2={route.toX}
              y2={route.toY}
              stroke="hsl(0, 0%, 90%)"
              strokeWidth="0.8"
              strokeDasharray={route.type === 'dashed' ? '2,2' : undefined}
              markerEnd="url(#arrowhead)"
            />
          ))}

          {/* Players */}
          {diagram.players.map((player, i) => (
            <g key={`player-${i}`}>
              {player.type === 'offense' ? (
                <circle
                  cx={player.x}
                  cy={player.y}
                  r="3.5"
                  fill="none"
                  stroke="hsl(0, 0%, 95%)"
                  strokeWidth="0.8"
                />
              ) : (
                <>
                  <line x1={player.x - 2.5} y1={player.y - 2.5} x2={player.x + 2.5} y2={player.y + 2.5} stroke="hsl(0, 84%, 60%)" strokeWidth="0.8" />
                  <line x1={player.x + 2.5} y1={player.y - 2.5} x2={player.x - 2.5} y2={player.y + 2.5} stroke="hsl(0, 84%, 60%)" strokeWidth="0.8" />
                </>
              )}
              {player.label && (
                <text
                  x={player.x}
                  y={player.y + (player.type === 'offense' ? 0.8 : 0.5)}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="hsl(0, 0%, 90%)"
                  fontSize="2.8"
                  fontFamily="Kanit"
                  fontWeight="600"
                >
                  {player.label}
                </text>
              )}
            </g>
          ))}

          {/* Arrow marker */}
          <defs>
            <marker id="arrowhead" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto">
              <polygon points="0 0, 6 2, 0 4" fill="hsl(0, 0%, 90%)" />
            </marker>
          </defs>
        </svg>
        {diagram.title && (
          <p className="text-center font-display text-sm text-secondary-foreground/70 mt-2">
            {diagram.title}
          </p>
        )}
      </div>
    </div>
  );
}
